import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronRight } from 'lucide-react';

import { ExceptionSeverityBadge } from '@/components/efs/ExceptionSeverityBadge';
import { formatDateTime } from '@/lib/format';
import { cn } from '@/lib/utils';

import { AuditIssueMessage, useIssueText } from './AuditIssueMessage';
import { AuditTargetContext, AuditTargetLink } from './AuditTargetLink';
import type { IssuePresentation, IssueSeverity } from './types';

/**
 * One audit issue, in the mandatory content order (spec-13 §7.1):
 * severity → localized title → explanation/manual note → monthly summary
 * sheet + record → section/field → next action → primary action → optional
 * technical disclosure. Tooltip-only explanations and truncated-UUID labels
 * are prohibited; long codes wrap.
 */
export const AuditIssueCard = ({
  presentation,
  statusLabel,
  extraContent,
  actions,
  showTechnicalDetails,
  className,
}: {
  presentation: IssuePresentation;
  /** Localized workflow status text (e.g. exception open/resolved). */
  statusLabel?: string | null;
  /** Surface-specific extra rows (e.g. supplier reply, decision). */
  extraContent?: ReactNode;
  /** Additional surface actions rendered next to the target action. */
  actions?: ReactNode;
  /** Permission gate for the raw technical disclosure. */
  showTechnicalDetails?: boolean;
  className?: string;
}) => {
  const { t } = useTranslation(['issues']);
  const { action } = useIssueText(presentation, { technical: !!showTechnicalDetails });

  return (
    <div
      className={cn('space-y-2 rounded-md border border-efs-border bg-efs-bg-panel p-3', className)}
    >
      <div className="flex flex-wrap items-center gap-2">
        <ExceptionSeverityBadge severity={presentation.severity as IssueSeverity} />
        {statusLabel ? (
          <span className="rounded bg-efs-bg-subtle px-1.5 py-0.5 text-xs text-efs-text-muted">
            {statusLabel}
          </span>
        ) : null}
      </div>

      <AuditIssueMessage presentation={presentation} showTechnicalDetails={!!showTechnicalDetails} />

      <AuditTargetContext target={presentation.target} className="space-y-0.5" />

      {presentation.detected_at ? (
        <p className="text-xs text-efs-text-muted">
          <span className="mr-1">{t('efs.issues.target.detected_at')}:</span>
          <time dateTime={presentation.detected_at} className="text-efs-text-primary">
            {formatDateTime(presentation.detected_at)}
          </time>
        </p>
      ) : null}

      {extraContent}

      {action ? (
        <p className="text-xs text-efs-text-primary">
          <span className="mr-1 text-efs-text-muted">{t('efs.issues.target.next_action')}:</span>
          {action}
        </p>
      ) : null}

      <div className="flex min-h-11 flex-wrap items-center gap-2">
        <AuditTargetLink presentation={presentation} actionLabel={action} />
        {actions}
      </div>

      {showTechnicalDetails ? <TechnicalDisclosure presentation={presentation} /> : null}
    </div>
  );
};

/**
 * Collapsed-by-default raw diagnostic view for authorized internal users:
 * legacy description, code, target UUIDs. Wraps inside its bounded box
 * instead of widening the page (spec-13 §12).
 */
export const TechnicalDisclosure = ({
  presentation,
  extra,
}: {
  presentation: IssuePresentation;
  extra?: Record<string, unknown>;
}) => {
  const { t } = useTranslation(['issues']);
  const [open, setOpen] = useState(false);

  const payload: Record<string, unknown> = {
    issue_code: presentation.issue_code,
    legacy_description: presentation.legacy_description ?? undefined,
    record_type: presentation.target?.record_type ?? undefined,
    record_id: presentation.target?.record_id ?? undefined,
    monthly_batch_id: presentation.target?.monthly_batch_id ?? undefined,
    ...extra,
  };

  return (
    <div className="border-t border-efs-border pt-1.5">
      <button
        type="button"
        className="flex min-h-6 items-center gap-1 text-xs text-efs-text-muted hover:text-efs-text-primary"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        {open ? (
          <ChevronDown className="h-3 w-3" aria-hidden />
        ) : (
          <ChevronRight className="h-3 w-3" aria-hidden />
        )}
        {t('efs.issues.target.technical_details')}
      </button>
      {open ? (
        <pre className="mt-1 max-h-48 overflow-auto whitespace-pre-wrap break-all rounded bg-efs-bg-subtle p-2 text-[11px] text-efs-text-muted">
          {JSON.stringify(payload, null, 2)}
        </pre>
      ) : null}
    </div>
  );
};
